import { CreditPlan } from '../Enums/credit-plan.enum';

export interface CreditWalletProps {
  id: string;
  tenantId: string;
  plan: CreditPlan | string;
  initialPlanCredits: number;
  planCredits: number;
  planCreditsExpireAt: string | null;
  purchasedCredits: number;
  overConsumptionCredits: number;
}

/**
 * CreditWallet — the wallet entity for a tenant.
 *
 * Encapsulates balance logic: total available credits,
 * overconsumption detection, and plan expiry formatting.
 * Zero Angular imports — pure TypeScript.
 */
export class CreditWallet {
  readonly id: string;
  readonly tenantId: string;
  readonly plan: CreditPlan | string;
  readonly initialPlanCredits: number;
  readonly planCredits: number;
  readonly planCreditsExpireAt: string | null;
  readonly purchasedCredits: number;
  readonly overConsumptionCredits: number;

  constructor(props: CreditWalletProps) {
    this.id = props.id;
    this.tenantId = props.tenantId;
    this.plan = props.plan;
    this.initialPlanCredits = props.initialPlanCredits;
    this.planCredits = props.planCredits;
    this.planCreditsExpireAt = props.planCreditsExpireAt;
    this.purchasedCredits = props.purchasedCredits;
    this.overConsumptionCredits = props.overConsumptionCredits;
  }

  /** Sum of plan credits and purchased credits available for consumption. */
  get totalAvailable(): number {
    return this.planCredits + this.purchasedCredits;
  }

  /** Sum of all credit buckets (plan + purchased + overconsumption). Shown in title and topbar badge. */
  get totalCredits(): number {
    return this.planCredits + this.purchasedCredits + this.overConsumptionCredits;
  }

  /** True when the tenant has consumed beyond their available balance. */
  get isOverconsuming(): boolean {
    return this.overConsumptionCredits > 0;
  }

  /** Plan credits usage as a 0–100 percentage of the initial allocation. */
  get planUsagePercent(): number {
    if (this.initialPlanCredits === 0) return 0;
    const used = this.initialPlanCredits - this.planCredits;
    return Math.min(100, Math.round((used / this.initialPlanCredits) * 100));
  }

  /** Expiry date parsed as a Date object, or null if not set. */
  get planExpiresAt(): Date | null {
    if (!this.planCreditsExpireAt) return null;
    return new Date(this.planCreditsExpireAt);
  }

  /** Start of the current billing cycle (one month before expiry). */
  get planCycleStartsAt(): Date | null {
    const d = this.planExpiresAt;
    if (!d) return null;
    const start = new Date(d);
    start.setMonth(start.getMonth() - 1);
    return start;
  }
}
